import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Pagination, Table } from "react-bootstrap";
import { debounce } from "lodash";
import searchIcon from "../../assets/search.svg";
import { useNavigate } from "react-router-dom";
import adminRoute from "../../services/adminRoute";
import { useAppContext } from "../../AppContext";
import DeleteProductModal from "../../components/Modal/DeleteProductModal";

const REVIEW_TABS = [
  { label: "All Reviews", isPublic: null },
  { label: "Active Reviews", isPublic: true },
  { label: "Deactivated Reviews", isPublic: false },
];

const AdminReviews = () => {
  const { showToast, setShowDeleteModal } = useAppContext();
  const { getAdminReviews } = adminRoute();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [activeTab, setActiveTab] = useState(REVIEW_TABS[0].label);
  const [currentReview, setCurrentReview] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchReview, setSearchReview] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalPages: 0,
    totalReviews: 0,
  });

  const tabFilter = REVIEW_TABS.find((tab) => tab.label === activeTab)?.isPublic ?? null;

  const fetchReviews = (page = pagination.currentPage) => {
    getAdminReviews(
      setLoading,
      showToast,
      setReviews,
      { ...pagination, currentPage: page },
      setPagination,
      tabFilter,
      debouncedSearch
    );
  };

  useEffect(() => {
    fetchReviews();
  }, [pagination.currentPage, activeTab, debouncedSearch]);

  useEffect(() => {
    const applySearch = debounce(() => {
      const nextSearch = searchReview.trim();
      if (nextSearch === debouncedSearch) return;
      setDebouncedSearch(nextSearch);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 1000);
    applySearch();
    return () => applySearch.cancel();
  }, [searchReview]);

  const handleTabClick = (tab) => {
    if (tab === activeTab && pagination.currentPage === 1) {
      fetchReviews(1);
      return;
    }
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleReviewUpdated = () => {
    if (reviews.length === 1 && pagination.currentPage > 1) {
      handlePageChange(pagination.currentPage - 1);
    } else {
      fetchReviews();
    }
  };

  const isReviewPublic = (review) => review?.public ?? review?.isPublic ?? tabFilter === true;

  const getCustomerName = (review) => {
    if (review?.user?.name) return review.user.name.trim();
    const fullName = [review?.user?.firstName, review?.user?.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(" ");
    return fullName || review?.user?.email || "-";
  };

  const getAverageRating = (review) => Number(review?.averageRating ?? review?.rating) || 0;

  const getOrderSku = (review) => review?.order?.sku || review?.orderSku || "-";

  const getOrderId = (review) => review?.order?._id || review?.order?.id || review?.orderId;

  const formatDateToLongForm = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Africa/Lagos",
    });
  };

  const renderStars = (rating) => {
    const value = Math.round((Number(rating) || 0) * 2) / 2;
    return (
      <span style={{ color: "#F2994A", whiteSpace: "nowrap" }}>
        {[1, 2, 3, 4, 5].map((star) => {
          const icon =
            star <= value ? "bi-star-fill" : star - 0.5 === value ? "bi-star-half" : "bi-star";
          return <i key={star} className={`bi ${icon} me-1`}></i>;
        })}
      </span>
    );
  };

  const formatRating = (rating) => {
    const value = Number(rating) || 0;
    return Number.isInteger(value) ? value.toFixed(1) : value.toFixed(2).replace(/0$/, "");
  };

  const ratingCategories = [
    { key: "freshnessRating", label: "Freshness" },
    { key: "deliveryRating", label: "Delivery" },
    { key: "supportRating", label: "Customer Support" },
    { key: "recommendationRating", label: "Recommendation" },
  ];

  const getReviewProducts = (review) => {
    const products = review?.products?.length ? review.products : review?.ratedProducts || [];
    const ratingsById = new Map(
      (review?.ratedProducts || []).map((product) => [product?.id, product?.rating])
    );
    return [...products]
      .map((product) => ({
        ...product,
        rating: ratingsById.get(product?.id) ?? product?.rating ?? null,
      }))
      .sort((a, b) => (a?.itemOrder ?? 0) - (b?.itemOrder ?? 0));
  };

  const openReviewDetails = (review) => {
    setSelectedReview(review);
    setShowDetailsModal(true);
  };

  const openStatusConfirm = (review) => {
    setShowDetailsModal(false);
    setCurrentReview({ ...review, public: isReviewPublic(review) });
    setShowDeleteModal(true);
  };

  const renderStatus = (review) => {
    const isPublic = isReviewPublic(review);
    const color = isPublic ? "#115F04" : "#F2994A";
    return (
      <span
        style={{
          backgroundColor: "white",
          color,
          border: `1px solid ${color}`,
          padding: "6px 16px",
          borderRadius: "12px",
          whiteSpace: "nowrap",
        }}
      >
        {isPublic ? "Active" : "Inactive"}
      </span>
    );
  };

  const cellStyle = { paddingTop: "15px", paddingBottom: "15px" };

  return (
    <section className="list-page">
      <div className="d-flex justify-content-between align-items-center header-admin py-5 pe-5 ps-4">
        <h3 style={{ color: "#344054" }}>Order Reviews</h3>
      </div>
      <div className="d-flex gap-5 pt-2 my-3 ps-4" style={{ borderBottom: "1px solid #EDEDED" }}>
        {REVIEW_TABS.map(({ label }) => (
          <p
            key={label}
            className={`order-item pb-2 mb-0 me-4 ${activeTab === label ? "active" : ""}`}
            onClick={() => handleTabClick(label)}
            style={{ cursor: "pointer", marginBottom: "5px" }}
          >
            {label}
          </p>
        ))}
      </div>
      <div className="ps-4 pe-5">
        <div className="d-flex align-items-center gap-5 py-4">
          <p style={{ color: "#1D2939", fontWeight: "500" }} className="my-auto">
            {pagination?.totalReviews || reviews?.length}{" "}
            {(pagination?.totalReviews || reviews?.length) === 1 ? "Review" : "Reviews"}
          </p>
          <div
            className="d-flex align-items-center position-relative search-bar py-1 px-1"
            style={{ border: "1px solid #D9D9D9", fontWeight: "500", width: "300px" }}
          >
            {!isFocused && searchReview === "" && (
              <img
                src={searchIcon}
                alt="search"
                style={{ top: "24%", left: "1.5%" }}
                className="position-absolute"
              />
            )}
            {searchReview !== "" && (
              <i
                className="bi bi-x"
                style={{
                  top: "5%",
                  right: "1.5%",
                  position: "absolute",
                  fontSize: "1.5rem",
                  color: "#98A2B3",
                  cursor: "pointer",
                }}
                onClick={() => setSearchReview("")}
              ></i>
            )}
            <Form.Control
              style={{ border: "none", backgroundColor: "transparent" }}
              placeholder="Search reviews"
              className="w-100"
              value={searchReview}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={(e) => setSearchReview(e.target.value)}
            />
          </div>
        </div>
      </div>
      {loading ? (
        <div
          className="justify-content-center align-items-center text-center"
          style={{ padding: "100px" }}
        >
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : reviews?.length > 0 ? (
        <div className="ps-4 pe-5 list-body">
          <div className="list-scroll">
            <Table hover className="my-3" id="review-table">
              <thead>
                <tr style={{ fontWeight: "600" }} className="mb-2">
                  <th style={{ fontWeight: "600" }} className="pb-3">
                    Customer
                  </th>
                  <th style={{ fontWeight: "600" }} className="pb-3">
                    Order ID
                  </th>
                  <th style={{ fontWeight: "600" }} className="pb-3">
                    Rating
                  </th>
                  <th style={{ fontWeight: "600" }} className="pb-3">
                    Comment
                  </th>
                  <th style={{ fontWeight: "600" }} className="pb-3">
                    Date
                  </th>
                  <th style={{ fontWeight: "600" }} className="pb-3">
                    Status
                  </th>
                  <th style={{ fontWeight: "600" }} className="pb-3"></th>
                  <th style={{ fontWeight: "600" }} className="pb-3"></th>
                </tr>
              </thead>
              <tbody className="py-2">
                {reviews.map((review) => {
                  const isPublic = isReviewPublic(review);
                  const orderId = getOrderId(review);
                  return (
                    <tr
                      key={review?._id || review?.id}
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "#333333",
                        cursor: "pointer",
                      }}
                      onClick={() => openReviewDetails(review)}
                    >
                      <td style={cellStyle}>
                        {getCustomerName(review)}
                        {review?.anonymous && (
                          <div style={{ fontSize: "12px", color: "#98A2B3" }}>Anonymous</div>
                        )}
                      </td>
                      <td
                        style={
                          orderId
                            ? {
                                ...cellStyle,
                                color: "#194298",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }
                            : cellStyle
                        }
                        onClick={(e) => {
                          if (!orderId) return;
                          e.stopPropagation();
                          navigate(`/admin/orders/${orderId}`);
                        }}
                      >
                        {getOrderSku(review)}
                      </td>
                      <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                        <i className="bi bi-star-fill me-2" style={{ color: "#F2994A" }}></i>
                        {formatRating(getAverageRating(review))}
                      </td>
                      <td style={{ ...cellStyle, maxWidth: "360px" }}>
                        <div
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            wordBreak: "break-word",
                          }}
                        >
                          {review?.comment || "-"}
                        </div>
                      </td>
                      <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                        {formatDateToLongForm(review?.createdAt)}
                      </td>
                      <td style={cellStyle}>{renderStatus(review)}</td>
                      <td style={cellStyle}>
                        <i
                          className={isPublic ? "bi bi-dash-circle" : "bi bi-plus-circle"}
                          title={isPublic ? "Deactivate" : "Activate"}
                          style={{
                            cursor: "pointer",
                            color: isPublic ? "red" : "green",
                            fontSize: "1.2rem",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatusConfirm(review);
                          }}
                        ></i>
                      </td>
                      <td
                        style={{
                          ...cellStyle,
                          cursor: "pointer",
                          textDecoration: "underline",
                          color: "#469B12",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openReviewDetails(review);
                        }}
                      >
                        View
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center py-3">
              <p style={{ fontWeight: "500", color: "#919EAB" }}>
                Showing {reviews.length} of {pagination.totalReviews} reviews
              </p>
              <Pagination className="custom-pagination d-flex gap-2">
                <button
                  className="btn btn-outline-secondary pb-0 px-2"
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  style={{
                    border: pagination.currentPage === 1 ? "none" : "1px solid #C4CDD5",
                    color: pagination.currentPage === 1 ? "#C4CDD5" : "#000000",
                  }}
                >
                  <h6>
                    <i className="bi bi-chevron-left"></i>
                  </h6>
                </button>

                <Pagination.Item
                  onClick={() => handlePageChange(1)}
                  active={pagination.currentPage === 1}
                >
                  1
                </Pagination.Item>

                {pagination.currentPage > 3 && <Pagination.Ellipsis disabled />}

                {pagination.currentPage > 2 && (
                  <Pagination.Item onClick={() => handlePageChange(pagination.currentPage - 1)}>
                    {pagination.currentPage - 1}
                  </Pagination.Item>
                )}

                {pagination.currentPage !== 1 &&
                  pagination.currentPage !== pagination.totalPages && (
                    <Pagination.Item active>{pagination.currentPage}</Pagination.Item>
                  )}

                {pagination.currentPage < pagination.totalPages - 1 && (
                  <Pagination.Item onClick={() => handlePageChange(pagination.currentPage + 1)}>
                    {pagination.currentPage + 1}
                  </Pagination.Item>
                )}

                {pagination.currentPage < pagination.totalPages - 2 && (
                  <Pagination.Ellipsis disabled />
                )}

                <Pagination.Item
                  onClick={() => handlePageChange(pagination.totalPages)}
                  active={pagination.currentPage === pagination.totalPages}
                >
                  {pagination.totalPages}
                </Pagination.Item>

                <button
                  className="btn btn-outline-secondary pb-0 px-2"
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  style={{
                    border:
                      pagination.currentPage === pagination.totalPages
                        ? "none"
                        : "1px solid #C4CDD5",
                    color: pagination.currentPage === pagination.totalPages ? "#C4CDD5" : "#000000",
                  }}
                >
                  <h6>
                    <i className="bi bi-chevron-right"></i>
                  </h6>
                </button>
              </Pagination>
            </div>
          )}
        </div>
      ) : (
        <div className="fw-medium px-5 py-5">
          <h4>
            {debouncedSearch
              ? "No reviews match your search"
              : tabFilter === true
                ? "No active reviews"
                : tabFilter === false
                  ? "No deactivated reviews"
                  : "No reviews yet"}
          </h4>
        </div>
      )}

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#344054", fontSize: "1.25rem" }}>
            Review Details
          </Modal.Title>
        </Modal.Header>
        {selectedReview && (
          <Modal.Body className="px-4 pb-4">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
              <div>
                <h5 className="mb-1" style={{ color: "#1D2939", fontWeight: "700" }}>
                  {getCustomerName(selectedReview)}
                </h5>
                {selectedReview?.user?.email && (
                  <p
                    className="mb-1"
                    style={{ color: "#475467", fontSize: "14px", fontWeight: "500" }}
                  >
                    {selectedReview.user.email}
                  </p>
                )}
                <p
                  className="mb-0"
                  style={{ color: "#475467", fontSize: "14px", fontWeight: "500" }}
                >
                  Order {getOrderSku(selectedReview)} &middot;{" "}
                  {formatDateToLongForm(selectedReview?.createdAt)}
                  {selectedReview?.anonymous && " · Posted anonymously"}
                </p>
              </div>
              <div className="d-flex flex-column align-items-end gap-3">
                {renderStatus(selectedReview)}
                {selectedReview?.publishedAt && (
                  <span style={{ color: "#667085", fontSize: "12px", whiteSpace: "nowrap" }}>
                    Published on {formatDateToLongForm(selectedReview?.publishedAt)}
                  </span>
                )}
              </div>
            </div>

            <div
              className="d-flex align-items-center gap-3 p-3 mb-3"
              style={{ backgroundColor: "#F9FAFB", borderRadius: "12px" }}
            >
              <h2 className="mb-0" style={{ color: "#1D2939" }}>
                {formatRating(getAverageRating(selectedReview))}
              </h2>
              <div>
                {renderStars(getAverageRating(selectedReview))}
                <div style={{ color: "#667085", fontSize: "13px" }}>Average rating</div>
              </div>
            </div>

            <div className="mb-4">
              {ratingCategories.map(({ key, label }) => {
                const value = selectedReview?.[key];
                const rated = typeof value === "number" && value > 0;
                return (
                  <div
                    key={key}
                    className="d-flex justify-content-between align-items-center py-2"
                    style={{ borderBottom: "1px solid #EDEDED", fontSize: "14px" }}
                  >
                    <span style={{ color: "#344054", fontWeight: "500" }}>{label}</span>
                    {rated ? (
                      <span className="d-flex align-items-center gap-2">
                        {renderStars(value)}
                        <span style={{ color: "#667085", minWidth: "28px" }}>{value}/5</span>
                      </span>
                    ) : (
                      <span style={{ color: "#98A2B3" }}>Not rated</span>
                    )}
                  </div>
                );
              })}
            </div>

            {getReviewProducts(selectedReview).length > 0 && (
              <div className="mb-4">
                <h6 style={{ color: "#344054", fontWeight: "600" }}>Rated Products</h6>
                {getReviewProducts(selectedReview).map((product) => {
                  const rated = typeof product?.rating === "number" && product.rating > 0;
                  return (
                    <div
                      key={product?.id}
                      className="d-flex justify-content-between align-items-center gap-3 py-2"
                      style={{ borderBottom: "1px solid #EDEDED", fontSize: "14px" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        {product?.image && (
                          <img
                            src={product.image}
                            alt={product?.productName}
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        )}
                        <span style={{ color: "#344054", fontWeight: "500" }}>
                          {product?.productName?.trim()}
                          {product?.size && ` - ${product.size.trim()}`}
                        </span>
                      </div>
                      {rated ? (
                        <span className="d-flex align-items-center gap-2">
                          {renderStars(product.rating)}
                          <span style={{ color: "#667085", minWidth: "28px" }}>
                            {product.rating}/5
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "#98A2B3", whiteSpace: "nowrap" }}>Not rated</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <h6 style={{ color: "#344054", fontWeight: "600" }}>Comment</h6>
            <p
              style={{
                color: "#333333",
                fontSize: "17px",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {selectedReview?.comment || "No comment left."}
            </p>

            <div className="d-flex justify-content-end gap-3 mt-4">
              <Button variant="outline-secondary" onClick={() => setShowDetailsModal(false)}>
                Close
              </Button>
              <Button
                variant={isReviewPublic(selectedReview) ? "danger" : "success"}
                onClick={() => openStatusConfirm(selectedReview)}
              >
                {isReviewPublic(selectedReview) ? "Deactivate" : "Activate"}
              </Button>
            </div>
          </Modal.Body>
        )}
      </Modal>

      <DeleteProductModal
        item={currentReview}
        itemType="review"
        actionType="deactivate"
        onSuccess={handleReviewUpdated}
      />
    </section>
  );
};

export default AdminReviews;
